import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { SortCriterion } from '@/entities/board';
import { useIsMobile } from '@/shared/lib/media';
import { Dialog, Icons } from '@/shared/ui';

import styles from './Kanban.module.css';

interface ColumnMenuProps {
  columnName: string;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  hasDescription: boolean;
  onRename: () => void;
  onMove: (direction: 'left' | 'right') => void;
  onEditDescription: () => void;
  onEditAppearance: () => void;
  onCopy: () => void;
  onMoveToBoard: () => void;
  onDelete: () => void;
  onSort: (criterion: SortCriterion) => void;
  /** The system Calendar column cannot be renamed, copied, moved to another board or deleted. */
  system?: boolean;
  /** Why sorting is unavailable right now (an active filter hides part of the column). */
  sortDisabledReason?: string | null;
}

const SORT_OPTIONS: readonly SortCriterion[] = [
  'title_asc',
  'title_desc',
  'deadline_asc',
  'deadline_desc',
  'created_asc',
  'created_desc',
  'incomplete_first',
  'completed_first',
];

/** Column actions; also the non-drag way to reorder columns. */
export function ColumnMenu({
  columnName,
  canMoveLeft,
  canMoveRight,
  hasDescription,
  onRename,
  onMove,
  onEditDescription,
  onEditAppearance,
  onCopy,
  onMoveToBoard,
  onDelete,
  onSort,
  system = false,
  sortDisabledReason = null,
}: ColumnMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const [sortOpen, setSortOpen] = useState(false);
  const mobile = useIsMobile();

  useEffect(() => {
    if (!open || mobile) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, mobile]);

  const run = (action: () => void) => () => {
    setOpen(false);
    setSortOpen(false);
    action();
  };

  const sortItems = (
    <div
      className={styles.sortGroup}
      role="group"
      aria-label={t('columns.sort.title')}
      data-testid="sort-options"
    >
      {SORT_OPTIONS.map((criterion) => (
        <button
          key={criterion}
          type="button"
          className={`${styles.menuItem} ${styles.sortItem}`}
          onClick={run(() => {
            onSort(criterion);
          })}
        >
          {t(`columns.sort.${criterion}`)}
        </button>
      ))}
      <p className={styles.sortNote}>{t('columns.sort.oneTime')}</p>
    </div>
  );

  const items = (
    <>
      {!system && (
        <button
          type="button"
          className={styles.menuItem}
          onClick={run(onRename)}
          autoFocus={!mobile}
        >
          <Icons.edit />
          {t('columns.rename')}
        </button>
      )}
      <button
        type="button"
        className={styles.menuItem}
        disabled={!canMoveLeft}
        onClick={run(() => {
          onMove('left');
        })}
      >
        <Icons.back />
        {t('columns.moveLeft')}
      </button>
      <button
        type="button"
        className={styles.menuItem}
        disabled={!canMoveRight}
        onClick={run(() => {
          onMove('right');
        })}
      >
        <Icons.chevronRight />
        {t('columns.moveRight')}
      </button>
      <button type="button" className={styles.menuItem} onClick={run(onEditDescription)}>
        <Icons.note />
        {t(hasDescription ? 'columns.editDescription' : 'columns.addDescription')}
      </button>
      <button type="button" className={styles.menuItem} onClick={run(onEditAppearance)}>
        <Icons.palette />
        {t('columns.appearanceMenu')}
      </button>
      {!system && (
        <button
          type="button"
          className={styles.menuItem}
          aria-expanded={sortOpen}
          aria-haspopup="true"
          disabled={sortDisabledReason !== null}
          title={sortDisabledReason ?? undefined}
          onClick={() => {
            setSortOpen((value) => !value);
          }}
        >
          <Icons.sort />
          {t('columns.sort.title')} {sortOpen ? '▾' : '▸'}
        </button>
      )}
      {!system && sortDisabledReason !== null && (
        <p className={styles.sortNote}>{sortDisabledReason}</p>
      )}
      {!system && sortOpen && sortDisabledReason === null && sortItems}
      {!system && (
        <>
          <button type="button" className={styles.menuItem} onClick={run(onCopy)}>
            <Icons.copy />
            {t('columns.copy')}
          </button>
          <button type="button" className={styles.menuItem} onClick={run(onMoveToBoard)}>
            <Icons.move />
            {t('columns.moveToBoard')}
          </button>
          <button
            type="button"
            className={`${styles.menuItem} ${styles.menuDanger}`}
            onClick={run(onDelete)}
          >
            <Icons.trash />
            {t('columns.delete')}
          </button>
        </>
      )}
    </>
  );

  return (
    <div
      ref={rootRef}
      className={styles.menuRoot}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          setOpen(false);
          toggleRef.current?.focus();
        }
      }}
    >
      <button
        ref={toggleRef}
        type="button"
        className={styles.iconButton}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('columns.actions', { name: columnName })}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        ⋯
      </button>
      {open &&
        mobile &&
        // A sheet instead of a popover: it can never end up partly off-screen.
        createPortal(
          <Dialog
            title={columnName}
            onClose={() => {
              setOpen(false);
              toggleRef.current?.focus();
            }}
          >
            <div
              className={styles.menuSheet}
              role="group"
              aria-label={t('columns.actions', { name: columnName })}
            >
              {items}
            </div>
          </Dialog>,
          document.body,
        )}
      {open && !mobile && (
        <div
          className={styles.menu}
          role="group"
          aria-label={t('columns.actions', { name: columnName })}
        >
          {items}
        </div>
      )}
    </div>
  );
}
